import React from "react";

/**
 * LoadingDots — three bouncing dots used for loading/searching states.
 *
 * Usage:
 *   <LoadingDots />                              → "Lädt..."
 *   <LoadingDots label="Suche" />                → "Suche..."
 *   <LoadingDots label={null} />                 → just the dots, no text
 *
 * The dots bounce sequentially (150ms apart). On dark backgrounds the dots
 * inherit the current text color, so place inside a colored container if you
 * want a specific shade.
 */
export function LoadingDots({
  label = "Lädt",
  size = "sm",
  className = "",
  "data-testid": testId,
}) {
  const dotSizeClass = size === "lg" ? "w-1.5 h-1.5" : "w-1 h-1";
  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      role="status"
      aria-live="polite"
      aria-label={label ? `${label}…` : "Lädt…"}
      data-testid={testId || "loading-dots"}
    >
      {label && <span>{label}</span>}
      <span className="inline-flex gap-0.5" aria-hidden="true">
        <span className={`${dotSizeClass} rounded-full bg-current animate-bounce`} style={{ animationDelay: "0ms" }} />
        <span className={`${dotSizeClass} rounded-full bg-current animate-bounce`} style={{ animationDelay: "150ms" }} />
        <span className={`${dotSizeClass} rounded-full bg-current animate-bounce`} style={{ animationDelay: "300ms" }} />
      </span>
    </span>
  );
}

/**
 * Block-level loader — centered with the spinner and an optional title/subtitle.
 * Use for skeleton-replacement when you want to show explicit progress text.
 */
export function LoadingBlock({ title = "Lädt", subtitle, className = "", "data-testid": testId }) {
  return (
    <div
      className={`flex flex-col items-center justify-center text-center py-12 text-[var(--text-3)] ${className}`}
      role="status"
      aria-live="polite"
      data-testid={testId || "loading-block"}
    >
      <LoadingDots label={null} size="lg" className="text-[var(--gold)] mb-3" />
      <div className="text-sm font-medium text-[var(--text-2)]">
        <LoadingDots label={title} size="sm" />
      </div>
      {subtitle && <div className="text-xs mt-1">{subtitle}</div>}
    </div>
  );
}
